import { EngineFailure } from '../../core/errors';
import { sanitizeFileName } from '../../core/filename';

/**
 * SVG 栅格化前置处理：解析内在尺寸并按需注入宽高属性。
 *
 * 纯文本变换，便于单测；真正的位图化交给 codec.decode（createImageBitmap 支持 SVG）。
 */

export interface SvgDimensions {
  width: number;
  height: number;
  fromViewBox: boolean;
}

const DEFAULT_SIZE = { width: 512, height: 512 };

function parseLength(value: string | undefined): number | null {
  if (!value) return null;
  const trimmed = value.trim();
  const match = /^(\d+(?:\.\d+)?)(px|pt|%)?$/.exec(trimmed);
  if (!match) return null;
  const parsed = Number.parseFloat(match[1]);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return parsed;
}

export function parseSvgDimensions(svg: string): SvgDimensions {
  const openTag = /<svg\b[^>]*>/i.exec(svg)?.[0];
  if (!openTag) throw new EngineFailure('DECODE_FAILED', 'SVG 缺少 <svg> 根元素');

  const width = parseLength(/width\s*=\s*"([^"]*)"/i.exec(openTag)?.[1]);
  const height = parseLength(/height\s*=\s*"([^"]*)"/i.exec(openTag)?.[1]);
  if (width && height) return { width, height, fromViewBox: false };

  const viewBox = /viewBox\s*=\s*"([^"]*)"/i.exec(openTag)?.[1];
  if (viewBox) {
    const parts = viewBox.trim().split(/[\s,]+/).map(Number);
    if (parts.length === 4 && parts.every((value) => Number.isFinite(value)) && parts[2] > 0 && parts[3] > 0) {
      const ratioWidth = width ?? (height ? Math.round((height * parts[2]) / parts[3]) : parts[2]);
      const ratioHeight = height ?? (width ? Math.round((width * parts[3]) / parts[2]) : parts[3]);
      return { width: ratioWidth, height: ratioHeight, fromViewBox: true };
    }
  }

  return { ...DEFAULT_SIZE, fromViewBox: false };
}

export interface SvgPrepareOptions {
  width?: number;
  height?: number;
}

export interface PreparedSvg {
  bytes: Uint8Array;
  width: number;
  height: number;
}

/** 按目标尺寸重写宽高属性后输出可栅格化的 SVG 字节。 */
export function prepareSvg(svg: string, options: SvgPrepareOptions = {}): PreparedSvg {
  const intrinsic = parseSvgDimensions(svg);
  const width = Math.round(options.width ?? intrinsic.width);
  const height = Math.round(options.height ?? intrinsic.height);
  if (width <= 0 || height <= 0) throw new EngineFailure('DECODE_FAILED', 'SVG 尺寸无效');

  const withSize = svg.replace(/<svg\b[^>]*>/i, (tag) => {
    const cleaned = tag
      .replace(/\swidth\s*=\s*"[^"]*"/i, '')
      .replace(/\sheight\s*=\s*"[^"]*"/i, '');
    return cleaned.replace(/<svg/i, `<svg width="${width}" height="${height}"`);
  });

  return { bytes: new TextEncoder().encode(withSize), width, height };
}

export function isSvgExtension(extension: string): boolean {
  return extension.toLowerCase() === 'svg';
}

export function svgOutputBaseName(fileName: string): string {
  return sanitizeFileName(fileName).replace(/\.svg$/i, '') || 'converted';
}
